import React, { useState, useEffect, useRef } from 'react'
import './Login.css'
import { useNavigate } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'

const ETNowLogin = () => {
  const BASE_URL = 'https://marketapi.etnownews.com'
  const [currentScreen, setCurrentScreen] = useState('phone')

  const [phoneNumber, setPhoneNumber] = useState('')
  const [otpValues, setOtpValues] = useState(['', '', '', ''])
  const [fullName, setFullName] = useState('')
  const [username, setUsername] = useState('')

  const [interests, setInterests] = useState({
    equity: false,
    fno: false,
    intraday: false,
    mf: false,
    commodities: false,
    global: false,
    ipo: false,
  })

  const [resendTimer, setResendTimer] = useState(28)
  const [isResendDisabled, setIsResendDisabled] = useState(true)

  const [apiToken, setApiToken] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [apiError, setApiError] = useState('')

  const otpFieldsRef = [useRef(null), useRef(null), useRef(null), useRef(null)]

  const navigate = useNavigate()

  useEffect(() => {
    let intervalId
    if (currentScreen === 'otp' && resendTimer > 0) {
      intervalId = setInterval(() => {
        setResendTimer((prev) => prev - 1)
      }, 1000)
    } else if (resendTimer === 0) {
      setIsResendDisabled(false)
    }
    return () => clearInterval(intervalId)
  }, [currentScreen, resendTimer])

  const startTimerCountdown = () => {
    setResendTimer(28)
    setIsResendDisabled(true)
  }

  const handlePhoneFormSubmit = async (e) => {
    e.preventDefault()
    if (phoneNumber.length !== 10) return

    setIsLoading(true)
    setApiError('')

    try {
      const res = await fetch(`${BASE_URL}/api/v1/accounts/expert_login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account: {
            full_phone_number: `91${phoneNumber}`,
            message_type: 'whatsapp',
          },
        }),
      })
      const data = await res.json()

      if (data?.message === "This account is not authorized to access the Expert Portal.") {
        toast.error(data.message)
        return
      }

      if (!res.ok || data?.message?.includes('Only expert')) {
        toast.error(
          data?.message ||
            data?.error ||
            'Something went wrong'
        )
        return
      }

      if (data.token) {
        setApiToken(data.token)
        console.log('Temporary phone session token saved in state.')
      }
      setCurrentScreen('otp')
      startTimerCountdown()
      setOtpValues(['', '', '', ''])
    } catch (err) {
      toast.error('Network error. Please check your connection and try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpPaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('Text').trim()

    if (!/^\d+$/.test(pastedData)) return

    const digits = pastedData.slice(0, 4).split('')
    const newOtp = [...otpValues]

    digits.forEach((digit, index) => {
      newOtp[index] = digit
    })

    setOtpValues(newOtp)

    const lastIndex = digits.length - 1
    if (otpFieldsRef[lastIndex]) {
      otpFieldsRef[lastIndex].current?.focus()
    }

    if (digits.length === 4) {
      verifyOtp(digits)
    }
  }

  const handleOtpInputChange = (e, index) => {
    const keyVal = e.key
    const currentOtpState = [...otpValues]

    if (keyVal === 'Backspace') {
      e.preventDefault()
      currentOtpState[index] = ''
      setOtpValues(currentOtpState)
      if (index > 0) {
        otpFieldsRef[index - 1].current.focus()
      }
      return
    }

    if (!/[0-9]/.test(keyVal)) {
      e.preventDefault()
      return
    }

    e.preventDefault()
    currentOtpState[index] = keyVal
    setOtpValues(currentOtpState)
    setApiError('')

    if (index < 3) {
      otpFieldsRef[index + 1].current.focus()
    }

    const finalCheckArray = [...currentOtpState]
    finalCheckArray[index] = keyVal
    if (finalCheckArray.every((val) => val !== '')) {
      setTimeout(() => {
        verifyOtp(finalCheckArray)
      }, 300)
    }
  }

  const verifyOtp = async (otpArr) => {
    const otp = otpArr.join('')
    if (otp.length !== 4) return

    setIsLoading(true)
    setApiError('')

    try {
      const res = await fetch(`${BASE_URL}/api/v1/accounts/onboard_verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: apiToken, otp }),
      })
      const data = await res.json()

      if (!res.ok) {
        const errorMsg =
          (typeof data?.message === 'object' ? data?.message?.message : data?.message) ||
          data?.error ||
          'Invalid OTP. Please try again.'

        setApiError(errorMsg)
        toast.error(errorMsg)
        setOtpValues(['', '', '', ''])
        otpFieldsRef[0].current?.focus()
        return
      }

      const finalAuthToken =
        data.authorization ||
        data?.data?.authorization ||
        data.token ||
        data?.data?.token

      if (finalAuthToken) {
        localStorage.setItem('auth_token', finalAuthToken)
        const expiryTime = Date.now() + 24 * 60 * 60 * 1000
        localStorage.setItem('token_expiry', expiryTime.toString())
        console.log(
          'Final Authorization Token saved in storage:',
          finalAuthToken
        )
      } else {
        console.error(
          'Token extraction failed from API keys:',
          Object.keys(data)
        )
      }

      if (data.first_name) {
        localStorage.setItem('user_first_name', data.first_name)
        localStorage.setItem('user_last_name', data.last_name || '')
      } else if (data?.data?.first_name) {
        localStorage.setItem('user_first_name', data.data.first_name)
        localStorage.setItem('user_last_name', data.data.last_name || '')
      }

      setTimeout(() => {
        navigate('/questions?tab=public')
      }, 150)
    } catch (err) {
      const fallbackMsg = 'Invalid OTP. Please try again.'
      setApiError(fallbackMsg)
      toast.error(fallbackMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOtpManualBtn = () => {
    if (otpValues.every((val) => val !== '')) {
      verifyOtp(otpValues)
    } else {
      alert('Please enter 4-digit code')
    }
  }

  const handleResendOtp = async () => {
    setIsLoading(true)
    setApiError('')
    setOtpValues(['', '', '', ''])
    startTimerCountdown()

    try {
      const res = await fetch(`${BASE_URL}/api/v1/accounts/expert_login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account: {
            full_phone_number: `91${phoneNumber}`,
            message_type: 'whatsapp',
          },
        }),
      })
      const data = await res.json()
      if (data.token) setApiToken(data.token)
    } catch (err) {
      setApiError('Failed to resend OTP. Please try again.')
    } finally {
      setIsLoading(false)
      otpFieldsRef[0].current?.focus()
    }
  }

  const formatTimeOutput = (secondsLeft) => {
    const structuralMinutes = Math.floor(secondsLeft / 60)
    const structuralSeconds = secondsLeft % 60
    return `${structuralMinutes}:${structuralSeconds
      .toString()
      .padStart(2, '0')}`
  }

  return (
    <div className='login-page-body-mock'>
      <Toaster position='top-right' reverseOrder={false} />
      <div className='login-container'>
        <div
          className={`screen ${currentScreen === 'phone' ? 'active' : ''}`}
          id='screen-phone'
        >
          <div className='login-header'>
            <div className='login-title'>ET Now Expert Q&amp;A</div>
            <div className='login-subtitle'>Rise with India</div>
            <div className='progress-bar'>
              <div className='progress-fill' style={{ width: '33%' }}></div>
            </div>
          </div>

          <div className='step-indicator'>Step 1 of 2</div>

          <div style={{ marginBottom: '24px' }}>
            <h2
              style={{ fontSize: '18px', marginBottom: '8px', fontWeight: 600 }}
            >
              Enter your number
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              We'll send a one-time code on WhatsApp
            </p>
          </div>

          {apiError && currentScreen === 'phone' && (
            <div
              style={{
                color: '#e53e3e',
                fontSize: '13px',
                marginBottom: '12px',
                padding: '8px 12px',
                background: '#fff5f5',
                borderRadius: '6px',
                border: '1px solid #fed7d7',
              }}
            >
              {apiError}
            </div>
          )}

          <form onSubmit={handlePhoneFormSubmit}>
            <div className='form-group'>
              <label className='form-label'>Mobile Number</label>
              <div className='phone-input-wrapper'>
                <div className='country-code'>+91</div>
                <input
                  type='tel'
                  className='form-input phone-number'
                  id='phone'
                  placeholder='98765 43210'
                  maxLength='10'
                  pattern='[0-9]{10}'
                  required
                  value={phoneNumber}
                  onChange={(e) =>
                    setPhoneNumber(e.target.value.replace(/\D/g, ''))
                  }
                />
              </div>
            </div>

            <button
              type='submit'
              className='button primary'
              disabled={isLoading || phoneNumber.length !== 10}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {isLoading ? 'Sending OTP...' : 'Send OTP on WhatsApp'}
            </button>
          </form>

          {/* <div className='terms-text'>
            By continuing, you agree to ET Now's{' '}
            <a href='#terms'>Terms of Use</a> and{' '}
            <a href='#privacy'>Privacy Policy</a>
          </div> */}
        </div>

        <div
          className={`screen ${currentScreen === 'otp' ? 'active' : ''}`}
          id='screen-otp'
        >
          <div className='login-header'>
            <button
              className='back-btn'
              onClick={() => {
                setCurrentScreen('phone')
                setApiError('')
              }}
            >
              <img
                src='/LeftArrow.png'
                alt='back'
                className='back-icon'
                style={{ width: '20px', height: '20px' }}
              />
            </button>
            <div className='login-title'>ET Now Expert Q&amp;A</div>
            <div className='login-subtitle'>Rise with India</div>
            <div className='progress-bar'>
              <div className='progress-fill' style={{ width: '66%' }}></div>
            </div>
          </div>

          <div className='step-indicator'>Step 2 of 3</div>

          <h2
            style={{ fontSize: '18px', marginBottom: '8px', fontWeight: 600 }}
          >
            Enter the code
          </h2>
          <p
            style={{
              fontSize: '13px',
              color: 'var(--text-secondary)',
              marginBottom: '20px',
            }}
          >
            4-digit code sent to your WhatsApp
          </p>

          <div className='otp-info'>
            <span
              className='whatsapp-icon'
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                marginRight: '6px',
              }}
            >
              <img src='/whatsApp.png' alt='whatsapp' width='30' height='30' />
            </span>
            Code sent to&nbsp;
            <strong id='phone-display'>
              +91 {phoneNumber || '98765 43210'}
            </strong>
          </div>

          {apiError && currentScreen === 'otp' && (
            <div
              style={{
                color: '#e53e3e',
                fontSize: '13px',
                marginBottom: '12px',
                padding: '8px 12px',
                background: '#fff5f5',
                borderRadius: '6px',
                border: '1px solid #fed7d7',
              }}
            >
              {apiError}
            </div>
          )}

          <div className='form-group'>
            <label className='form-label'>4-digit code</label>
            <div className='otp-inputs'>
              {[0, 1, 2, 3].map((idx) => (
                <input
                  key={idx}
                  type='text'
                  className={`otp-input ${
                    otpValues[idx] !== '' ? 'filled' : ''
                  }`}
                  maxLength='1'
                  pattern='[0-9]'
                  inputMode='numeric'
                  value={otpValues[idx]}
                  ref={otpFieldsRef[idx]}
                  onPaste={handleOtpPaste}
                  onKeyDown={(e) => handleOtpInputChange(e, idx)}
                  onChange={() => {}}
                  disabled={isLoading}
                />
              ))}
            </div>
          </div>

          <div className='resend-info'>
            <span>
              Resend in <span id='timer'>{formatTimeOutput(resendTimer)}</span>
            </span>
            <button
              className='resend-btn'
              id='resend-btn'
              disabled={isResendDisabled || isLoading}
              onClick={handleResendOtp}
            >
              {isResendDisabled ? 'Change number' : 'Resend OTP'}
            </button>
          </div>

          <button
            className='button primary'
            onClick={handleVerifyOtpManualBtn}
            disabled={isLoading}
          >
            {isLoading ? 'Verifying...' : 'Verify code'}
          </button>

          {/* <div className='terms-text' style={{ marginTop: '12px' }}>
            Code auto-fills if WhatsApp notifications are on
          </div> */}
        </div>
      </div>
    </div>
  )
}

export default ETNowLogin